import { Component } from '@theme/component';
import { ClothRenderer, spring, railTarget, hitTestRail, railPhotoBlend, railPhotoViews } from '@theme/pawl-cloth-renderer';

class PawlClothesRail extends Component {
  /** @type {{viewport?: HTMLElement, canvas?: HTMLCanvasElement, previous?: HTMLButtonElement, next?: HTMLButtonElement}} */
  refs;

  connectedCallback() {
    super.connectedCallback();
    this._schedule();
  }

  updatedCallback() {
    super.updatedCallback();
    this._stop();
    this._schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stop();
    this._waiting?.disconnect();
  }

  _schedule() {
    this._waiting?.disconnect();
    const ready = () => {
      if (!this.isConnected || !this.querySelector('[data-rail-end]')) return false;
      this._waiting?.disconnect();
      this._start();
      return true;
    };
    if (!ready()) {
      this._waiting = new MutationObserver(ready);
      this._waiting.observe(this, { childList: true, subtree: true });
    }
  }

  _start() {
    if (this._abort) return;
    this._viewport = this.querySelector('[ref="viewport"]');
    this._canvas = this.querySelector('canvas');
    this._items = [...this.querySelectorAll('[data-rail-item]')];
    if (!this._viewport || !this._canvas || !this._items.length) return;
    this._abort = new AbortController();
    const signal = this._abort.signal;
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal });
    this._states = this._items.map(() => ({ angle: 0, velocity: 0, yaw: 1.38, yawVelocity: 0, x: 0, xVelocity: 0, scale: 1, scaleVelocity: 0, energy: 0, pointer: 0, tagAngle: 0, tagVelocity: 0 }));
    this._items.forEach(item => {
      const pair = item.querySelector('[data-rail-pair-photo]');
      if (!pair) return;
      const fallback = () => {
        item.removeAttribute('data-photographic');
        const photo = item.querySelector('[data-cloth-photo]');
        if (photo) photo.loading = 'eager';
      };
      listen(pair, 'error', fallback);
      if (pair.complete && !pair.naturalWidth) fallback();
    });
    this._spring = spring;
    this._active = -1;
    this._visible = true;
    this._reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this._lastScroll = this._viewport.scrollLeft;
    listen(this._reduced, 'change', () => this._motionChanged());
    listen(this._viewport, 'pointerover', event => {
      if (event.pointerType === 'touch' || this._isTag(event.target)) return;
      const index = this._hit(event) >= 0 ? this._hit(event) : this._index(event.target);
      if (index >= 0) this._select(index);
    });
    listen(this, 'pointerleave', event => {
      if (event.pointerType !== 'touch' && !this._drag && !this.contains(document.activeElement)) this._close();
    });
    listen(this._viewport, 'focusin', event => {
      const index = this._index(event.target);
      if (index < 0) return;
      this._select(index);
      this._reveal(index, false);
    });
    listen(this._viewport, 'focusout', event => {
      if (!this.contains(event.relatedTarget)) this._close();
    });
    listen(this._viewport, 'pointerdown', event => {
      this._interacted = true;
      this._pointerType = event.pointerType;
      if (this._isTag(event.target)) return;
      if (event.button !== 0 || event.pointerType === 'touch') return;
      const index = this._hit(event) >= 0 ? this._hit(event) : this._index(event.target);
      if (index >= 0) this._select(index);
      this._drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, item: index, scroll: this._viewport.scrollLeft, moved: false };
    });
    listen(window, 'pointermove', event => this._pointerMove(event), { passive: false });
    const end = () => {
      if (this._drag?.moved) {
        this._suppressClick = performance.now() + 400;
        this.removeAttribute('data-dragging');
      }
      this._drag = null;
    };
    listen(window, 'pointerup', end);
    listen(window, 'pointercancel', end);
    listen(this._viewport, 'dragstart', event => event.preventDefault());
    listen(this._viewport, 'click', event => this._click(event));
    listen(this._viewport, 'scroll', () => {
      const change = this._viewport.scrollLeft - this._lastScroll;
      this._lastScroll = this._viewport.scrollLeft;
      if (!this._reduced.matches) for (const state of this._states) { state.velocity += Math.max(-0.3, Math.min(0.3, change * 0.006)); state.energy = Math.min(1, state.energy + Math.abs(change) * 0.009); }
      this._measure();
      this._controls();
      this._wake();
    }, { passive: true });
    listen(document, 'visibilitychange', () => { if (document.hidden) this._pause(); else { this._welcomeIfEligible(); this._wake(); } });
    listen(window, 'pageshow', () => { this.removeAttribute('data-leaving'); this._leaving = false; this._wake(); });
    listen(document, 'shopify:block:select', event => {
      const item = this._items.findIndex(node => node.dataset.blockId === event.detail?.blockId);
      if (item >= 0) { this._select(item); this._reveal(item); }
    });
    listen(this._canvas, 'webglcontextlost', event => this._contextLost(event));
    listen(this._canvas, 'webglcontextrestored', () => this._enhance());
    this._resize = new ResizeObserver(() => { this._measure(); this._controls(); this._wake(); });
    this._resize.observe(this._viewport);
    if ('IntersectionObserver' in window) {
      this._intersection = new IntersectionObserver(entries => {
        this._visible = entries[0].isIntersecting;
        if (this._visible) { this._enhance(); this._wake(); } else this._pause();
      }, { rootMargin: '180px' });
      this._intersection.observe(this);
    } else this._enhance();
    this._measure();
    this._controls();
    this._states.forEach((state, index) => this._paintRig(state, index));
    this.setAttribute('data-ready', '');
    if (!this._welcomeDone && 'IntersectionObserver' in window) {
      this._welcomeObserver = new IntersectionObserver(entries => {
        this._welcomeIntersecting = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.12;
        this._welcomeIfEligible();
      }, { threshold: [0, 0.12] });
      this._welcomeObserver.observe(this);
    }
  }

  _welcomeIfEligible() {
    if (this._welcomeDone || !this._welcomeIntersecting || document.hidden || !this._states) return;
    this._welcomeDone = true;
    this._welcomeObserver?.disconnect();
    if (this._reduced.matches || this._interacted || this._active >= 0) return;
    this._measure();
    const width = this._viewport?.clientWidth || 0;
    if (!width || !this._boxes) return;
    this._states.forEach((state, index) => {
      const box = this._boxes[index];
      if (!box || box.x < -box.size * 0.2 || box.x > width + box.size * 0.2) return;
      state.velocity += 0.15 * Math.sin(0.8 + index * 0.47);
      state.energy = Math.max(state.energy, 0.045);
    });
    this._wake();
  }

  _contextLost(event) {
    event.preventDefault();
    this._renderRun?.abort();
    this._renderRun = null;
    this._loading = false;
    this._pause();
    this.removeAttribute('data-renderer');
    this._renderer?.dispose();
    this._renderer = null;
    for (const item of this._items) item.removeAttribute('data-gpu-ready');
    this._wake();
  }

  _index(target) {
    return target instanceof Element ? this._items.indexOf(target.closest('[data-rail-item]')) : -1;
  }

  _isTag(target) {
    return target instanceof Element && Boolean(target.closest('[data-rail-tag]'));
  }

  _select(index) {
    if (index < 0 || index >= this._items.length) return;
    this._interacted = true;
    if (this._active !== index) {
      const direction = Math.sign(index - this._active) || 1;
      if (this._active >= 0) this._items[this._active].removeAttribute('data-selected');
      for (const state of this._states) state.pointer = 0;
      this._active = index;
      this._items[index].setAttribute('data-selected', '');
      this._states.forEach((state, position) => {
        const distance = Math.abs(position - index);
        const impulse = distance ? 0.34 * Math.exp(-(distance - 1) * 0.95) : 1;
        state.velocity = Math.max(-1.4, Math.min(1.4, state.velocity + direction * impulse));
        state.energy = Math.max(state.energy, distance ? 0.35 * Math.exp(-distance) : 0.85);
      });
      const title = this._items[index].querySelector('[data-rail-title]')?.textContent || '';
      const price = this._items[index].querySelector('[data-rail-price]')?.textContent || '';
      const caption = this.querySelector('[ref="caption"]');
      const link = this.querySelector('[ref="productLink"]');
      const position = this.querySelector('[ref="position"]');
      if (caption) caption.textContent = `${title} · ${price}`;
      if (link) { link.href = this._items[index].querySelector('a').href; link.hidden = false; }
      if (position) position.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this._items.length).padStart(2, '0')}`;
    }
    this._controls();
    this._wake();
  }

  _close() {
    if (this._active >= 0) this._items[this._active].removeAttribute('data-selected');
    for (const state of this._states || []) state.pointer = 0;
    this._active = -1;
    this._touchIndex = null;
    this._wake();
  }

  _hit(event) {
    if (!this._boxes || !this._canvas) return -1;
    const frame = this._canvas.getBoundingClientRect();
    return hitTestRail(this._boxes, this._states, event.clientX - frame.left, event.clientY - frame.top, this._active);
  }

  _pointerMove(event) {
    if (this._drag && this._drag.id === event.pointerId) {
      const dx = event.clientX - this._drag.x;
      if (Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(event.clientY - this._drag.y)) this._drag.moved = true;
      if (this._drag.moved) {
        event.preventDefault();
        this.setAttribute('data-dragging', '');
        this._viewport.scrollLeft = this._drag.scroll - dx;
        const state = this._states[this._drag.item];
        if (state && !this._reduced.matches) {
          const movement = event.clientX - this._drag.lastX;
          state.velocity += Math.max(-0.25, Math.min(0.25, movement * 0.008));
          state.energy = Math.min(1, state.energy + Math.abs(movement) * 0.02);
          this._wake();
        }
        this._drag.lastX = event.clientX;
      }
    } else if (event.pointerType !== 'touch' && this._viewport.contains(event.target) && !this._isTag(event.target)) {
      const index = this._hit(event);
      if (index >= 0) this._select(index);
      if (this._active < 0 || this._reduced.matches) return;
      const state = this._states[this._active];
      const box = this._boxes?.[this._active];
      if (box) {
        const frame = this._canvas.getBoundingClientRect();
        state.pointer = Math.max(-1, Math.min(1, (event.clientX - frame.left - box.x - state.x) / (box.size * 0.5)));
      }
      state.velocity = Math.max(-1.4, Math.min(1.4, state.velocity + Math.max(-0.09, Math.min(0.09, (event.movementX || 0) * 0.007))));
      state.energy = Math.min(1, state.energy + Math.abs(event.movementX || 0) * 0.003);
      this._wake();
    }
  }

  _click(event) {
    if (performance.now() < (this._suppressClick || 0)) { event.preventDefault(); return; }
    if (this._isTag(event.target)) return;
    const hit = this._hit(event);
    const index = hit >= 0 ? hit : this._index(event.target);
    if (index < 0 || event.detail === 0 || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || this._leaving) return;
    if ((event.pointerType === 'touch' || this._pointerType === 'touch') && this._touchIndex !== index) {
      event.preventDefault();
      this._select(index);
      this._reveal(index);
      this._touchIndex = index;
      return;
    }
    if (this._reduced.matches) return;
    const link = this._items[index].querySelector('a');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    event.preventDefault();
    this._select(index);
    this._leaving = true;
    this.setAttribute('data-leaving', '');
    this._wake();
    this._navigation = setTimeout(() => window.location.assign(link.href), 220);
  }

  previous() { this._advance(-1); }
  next() { this._advance(1); }

  _advance(direction) {
    const viewport = this._viewport;
    if (!viewport) return;
    const index = this._active < 0 ? (direction > 0 ? 0 : this._items.length - 1) : Math.max(0, Math.min(this._items.length - 1, this._active + direction));
    this._select(index);
    this._reveal(index);
  }

  _reveal(index, smooth = true) {
    const item = this._items[index], viewport = this._viewport;
    const width = viewport.clientWidth;
    let targetCenter = width / 2;
    if (width <= 749) {
      const size = this._boxes?.[index]?.size || 240;
      const ratio = item.dataset.shape === 'bib' ? 0.2 : 0.28;
      const tagWidth = item.querySelector('[data-rail-tag]')?.offsetWidth || 112;
      targetCenter = Math.max(60, Math.min(targetCenter, width - size * ratio - tagWidth - 16));
    }
    const left = item.offsetLeft + item.offsetWidth / 2 - targetCenter;
    viewport.scrollTo({ left, behavior: smooth && !this._reduced.matches ? 'smooth' : 'instant' });
  }

  _controls() {
    const previous = this.querySelector('[ref="previous"]'), next = this.querySelector('[ref="next"]');
    const max = this._viewport.scrollWidth - this._viewport.clientWidth;
    if (previous) previous.disabled = this._active === 0;
    if (next) next.disabled = this._active === this._items.length - 1;
    this.toggleAttribute('data-scrollable', max > 2);
  }

  _measure() {
    const frame = this._canvas.getBoundingClientRect();
    if (!frame.width || !frame.height) return;
    this._boxes = this._items.map(item => {
      const box = item.querySelector('.pawl-rail__visual').getBoundingClientRect();
      const hookY = Number.parseFloat(getComputedStyle(this).getPropertyValue('--rail-hook-y')) || 76;
      const photoViews = item.hasAttribute('data-photographic') ? railPhotoViews(item.querySelector('[data-rail-photo-layout]')?.dataset.railPhotoLayout) : null;
      const photoHeight = photoViews ? Math.max(...photoViews.map(view => (view.crop[1] + view.crop[3] - view.pivot[1]) * view.scale[1])) : 0;
      return { x: box.left - frame.left + box.width / 2, y: box.top - frame.top + hookY, size: box.width, kind: item.dataset.shape, photoHeight };
    });
    this._spacing = this._items[0].offsetWidth || 68;
    const mobile = matchMedia('(max-width: 749px)').matches;
    this._tagAnchors = this._items.map((item, index) => {
      const size = this._boxes[index].size;
      const bib = item.dataset.shape === 'bib';
      return { x: size * (bib ? (mobile ? 0.2 : 0.23) : (mobile ? 0.28 : 0.33)), y: size * (bib ? 0.7 : 0.38) };
    });
    if (this._renderer && (this._width !== frame.width || this._height !== frame.height)) this._renderer.resize(frame.width, frame.height);
    this._width = frame.width;
    this._height = frame.height;
  }

  async _enhance() {
    if (!this._abort || this._abort.signal.aborted || this._reduced.matches || this._renderer || this._loading || !this._visible) return;
    this._loading = true;
    const owner = this._abort;
    const run = new AbortController();
    const signal = run.signal;
    const abortRun = () => run.abort();
    this._renderRun = run;
    owner.signal.addEventListener('abort', abortRun, { once: true });
    let renderer;
    try {
      if (owner.signal.aborted) { run.abort(); return; }
      this._measure();
      renderer = new ClothRenderer(this._canvas);
      this._renderer = renderer;
      renderer.resize(this._width || 1, this._height || 1);
      this.setAttribute('data-renderer', 'webgl');
      await renderer.load(this._items, signal, index => {
        if (signal.aborted || this._renderRun !== run || this._renderer !== renderer || this._reduced.matches) return;
        renderer.draw(this._boxes || [], this._states, performance.now() / 1000, this._active, this._leaving);
        this._items[index].toggleAttribute('data-gpu-ready', Boolean(renderer.resources[index]));
        this._wake();
      });
      if (signal.aborted || this._renderRun !== run) return;
      this._measure();
      this._wake();
    } catch {
      if (this._renderer === renderer) {
        renderer?.dispose();
        this._renderer = null;
        this.removeAttribute('data-renderer');
        for (const item of this._items) item.removeAttribute('data-gpu-ready');
      }
    } finally {
      owner.signal.removeEventListener('abort', abortRun);
      if (this._renderRun === run) { this._renderRun = null; this._loading = false; }
    }
  }

  _motionChanged() {
    if (this._reduced.matches) {
      this._renderRun?.abort();
      this._renderRun = null;
      this._loading = false;
      this._pause();
      this._renderer?.dispose();
      this._renderer = null;
      this.removeAttribute('data-renderer');
      for (const item of this._items) item.removeAttribute('data-gpu-ready');
      this._snap();
    } else { this._enhance(); this._wake(); }
  }

  _wake() {
    if (this._reduced?.matches) { this._snap(); return; }
    if (this._frame || !this._states || !this._visible || document.hidden) return;
    this._lastTime = 0;
    this._frame = requestAnimationFrame(time => this._tick(time));
  }

  _tick(time) {
    this._frame = 0;
    const dt = this._lastTime ? Math.min(1 / 30, (time - this._lastTime) / 1000) : 1 / 60;
    this._lastTime = time;
    let moving = false;
    this._states.forEach((state, i) => {
      const target = railTarget(i, this._active, this._boxes?.[i]?.size || 260, this._spacing || 68, this._leaving);
      const yaw = target.yaw + (i === this._active ? (state.pointer || 0) * 0.12 : 0);
      const {x: offset, scale} = target;
      const roll = i === this._active ? (state.pointer || 0) * 0.035 : 0;
      [state.angle, state.velocity] = this._spring(state.angle, state.velocity, roll, dt, 48, 8);
      [state.yaw, state.yawVelocity] = this._spring(state.yaw, state.yawVelocity, yaw, dt);
      [state.x, state.xVelocity] = this._spring(state.x, state.xVelocity, offset, dt);
      [state.scale, state.scaleVelocity] = this._spring(state.scale, state.scaleVelocity, scale, dt);
      const tagTarget = Math.max(-0.04, Math.min(0.04, -state.angle * 0.42));
      [state.tagAngle, state.tagVelocity] = this._spring(state.tagAngle, state.tagVelocity, tagTarget, dt, 20, 6);
      state.tagAngle = Math.max(-0.045, Math.min(0.045, state.tagAngle));
      state.energy *= Math.exp(-dt * 3.2);
      state.angle = Math.max(-0.12, Math.min(0.12, state.angle));
      this._paintRig(state, i);
      moving ||= Math.abs(state.velocity) > 0.0007 || Math.abs(state.angle - roll) > 0.0002 || Math.abs(state.yaw - yaw) > 0.0003 || Math.abs(state.x - offset) > 0.02 || Math.abs(state.scale - scale) > 0.0002 || Math.abs(state.tagVelocity) > 0.0007 || Math.abs(state.tagAngle - tagTarget) > 0.0002 || state.energy > 0.001;
    });
    this._renderer?.draw(this._boxes || [], this._states, time / 1000, this._active, this._leaving);
    if (moving) this._frame = requestAnimationFrame(next => this._tick(next));
  }

  _pause() { cancelAnimationFrame(this._frame); this._frame = 0; }

  _paintRig(state, index) {
    const item = this._items[index];
    const rig = item.querySelector('.pawl-rail__rig');
    const anchor = this._tagAnchors?.[index];
    const cosine = Math.cos(state.angle), sine = Math.sin(state.angle);
    const scale = state.scale || 1;
    const shiftX = anchor ? scale * (anchor.x * cosine - anchor.y * sine) - anchor.x : 0;
    const shiftY = anchor ? scale * (anchor.x * sine + anchor.y * cosine) - anchor.y : 0;
    item.style.setProperty('--rail-tag-offset', `${state.x + shiftX}px`);
    item.style.setProperty('--rail-tag-offset-y', `${shiftY}px`);
    item.style.setProperty('--rail-tag-angle', `${state.angle}rad`);
    item.style.setProperty('--rail-tag-paper-sway', `${state.tagAngle || 0}rad`);
    rig.style.setProperty('--rail-sway', `${state.angle}rad`);
    rig.style.setProperty('--rail-yaw', `${state.yaw}rad`);
    rig.style.setProperty('--rail-offset', `${state.x}px`);
    rig.style.setProperty('--rail-scale', String(state.scale));
    if (this._items[index].hasAttribute('data-photographic')) {
      const front = railPhotoBlend(state.yaw);
      rig.style.setProperty('--rail-front-alpha', String(Math.min(1, front * 3)));
      rig.style.setProperty('--rail-side-alpha', String(Math.min(1, (1 - front) * 3)));
    }
  }

  _snap() {
    if (!this._states) return;
    this._states.forEach((state, index) => {
      Object.assign(state, railTarget(index, this._active, this._boxes?.[index]?.size || 260, this._spacing || 68), {angle: 0, velocity: 0, yawVelocity: 0, xVelocity: 0, scale: 1, scaleVelocity: 0, energy: 0, pointer: 0, tagAngle: 0, tagVelocity: 0});
      this._paintRig(state, index);
    });
  }

  _stop() {
    this._abort?.abort();
    this._abort = null;
    this._renderRun?.abort();
    this._renderRun = null;
    this._pause();
    clearTimeout(this._navigation);
    this._resize?.disconnect();
    this._intersection?.disconnect();
    this._welcomeObserver?.disconnect();
    this._welcomeIntersecting = false;
    this._renderer?.dispose();
    this._renderer = null;
    this._loading = false;
    this._drag = null;
    this._leaving = false;
    this._touchIndex = null;
    for (const attribute of ['data-renderer', 'data-ready', 'data-dragging', 'data-leaving']) this.removeAttribute(attribute);
    for (const item of this._items || []) {
      item.removeAttribute('data-selected');
      item.removeAttribute('data-gpu-ready');
      item.style.removeProperty('--rail-tag-offset');
      item.style.removeProperty('--rail-tag-angle');
      item.style.removeProperty('--rail-tag-offset-y');
      item.style.removeProperty('--rail-tag-paper-sway');
      item.querySelector('.pawl-rail__rig')?.removeAttribute('style');
    }
  }
}

if (!customElements.get('pawl-clothes-rail')) customElements.define('pawl-clothes-rail', PawlClothesRail);
